import { useState } from "react";
import { Link } from "react-router-dom";
import { isEmbedded } from "@/lib/tenant";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Calendar, User, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const fmtTime12h = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
};

const fmtDateLabel = (mondayStr, dayIdx) => {
  const [y, m, d] = mondayStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d + dayIdx);
  return dt.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
};

const ROLE_COLORS = {
  Produce: "bg-green-100 text-green-800 border-green-300",
  Meat: "bg-red-100 text-red-800 border-red-300",
  Deli: "bg-yellow-100 text-yellow-800 border-yellow-300",
  Cashier: "bg-blue-100 text-blue-800 border-blue-300",
  Grocery: "bg-purple-100 text-purple-800 border-purple-300",
  Manager: "bg-orange-100 text-orange-800 border-orange-300",
  Other: "bg-gray-100 text-gray-800 border-gray-300",
};

export default function MySchedulePage() {
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = async (codeToUse, weekOffset) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/schedule/my-schedule/${codeToUse}`, { params: { week_offset: weekOffset } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not find your schedule");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!code.trim()) return toast.error("Enter your code");
    setOffset(0);
    load(code.trim(), 0);
  };

  const changeWeek = (delta) => {
    const next = offset + delta;
    setOffset(next);
    load(code.trim(), next);
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1A1A1A] to-[#2D2D2D] flex flex-col">
        <header className="bg-[#1A1A1A] border-b border-[#333] py-4 px-4">
          <div className="max-w-md mx-auto flex justify-between items-center">
            {isEmbedded() ? <div className="w-6" /> : <Link to="/" className="text-white hover:text-[#D4AF37]"><ArrowLeft className="w-6 h-6" /></Link>}
            <h1 className="text-white font-bold text-lg">My Schedule</h1>
            <div className="w-6"></div>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#A61B1B] rounded-full flex items-center justify-center mx-auto mb-4"><User className="w-8 h-8 text-white" /></div>
              <h2 className="text-2xl font-bold">My Schedule</h2>
              <p className="text-[#666] mt-1">Enter your employee code</p>
            </div>
            <Input
              type="number"
              placeholder="Code (e.g., 7861)"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="text-center text-2xl font-bold h-14 mb-4"
              onKeyPress={(e) => e.key === "Enter" && handleSubmit()}
              data-testid="my-sched-code"
            />
            <Button onClick={handleSubmit} disabled={loading} className="w-full bg-[#A61B1B] hover:bg-[#8B1717] h-12 text-lg" data-testid="my-sched-submit">
              {loading ? "Loading..." : "View Schedule"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0] pb-12">
      <header className="bg-[#A61B1B] text-white py-4 px-4">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">Hi, {data.employee_name}</h1>
            <p className="text-sm opacity-90">Your shifts this week</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => { setData(null); setCode(""); }} className="border-white text-white hover:bg-white hover:text-[#A61B1B]">Switch</Button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-4">
        <div className="flex items-center justify-between mb-4">
          <Button size="sm" variant="outline" onClick={() => changeWeek(-1)} disabled={loading}><ChevronLeft className="w-4 h-4" /> Prev</Button>
          <div className="text-center">
            <div className="font-bold text-[#1A1A1A]">{data.week_start} → {data.week_end}</div>
            <div className="text-xs text-[#666]">{offset === 0 ? "This week" : offset > 0 ? "Future week" : "Past week"}</div>
          </div>
          <Button size="sm" variant="outline" onClick={() => changeWeek(1)} disabled={loading}>Next <ChevronRight className="w-4 h-4" /></Button>
        </div>

        {data.shifts.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center text-[#666]">
            <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No shifts scheduled for this week.</p>
            <p className="text-xs mt-1">Check with your manager.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {DAYS.map((day, idx) => {
              const dayShifts = data.shifts.filter(s => s.day_of_week === idx);
              if (dayShifts.length === 0) return null;
              return (
                <div key={idx} className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="font-bold text-[#1A1A1A] mb-2 text-sm">{fmtDateLabel(data.week_start, idx)}</div>
                  <div className="space-y-2">
                    {dayShifts.map(s => (
                      <div key={s.id} className={`rounded border p-3 ${ROLE_COLORS[s.role] || ROLE_COLORS.Other}`}>
                        <div className="flex justify-between items-center">
                          <div className="text-lg font-bold">{fmtTime12h(s.start_time)} – {fmtTime12h(s.end_time)}</div>
                          <div className="text-sm font-semibold">{s.role}</div>
                        </div>
                        {s.note && <div className="text-xs mt-1 opacity-80">{s.note}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            <div className="bg-[#1A5E1F] text-white rounded-lg p-4 text-center">
              <div className="text-xs opacity-90">Total scheduled hours this week</div>
              <div className="text-3xl font-bold">{data.total_hours}h</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
